import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight } from "lucide-react";
import { CATEGORIES, COOKING_INGREDIENTS, convertValue, cookingCategory, formatNumber } from "./units";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { UNIT_PAIRS, pairBySlug, pairTable, pairFormula, pairH1 } from "./unitPairs";
import { buildUnitSearch, parseUnitSearch } from "./unitLink";

const TOOL_INFO_CONTENT = {
  what: `Converts values between different units of measurement — length, weight, volume, area, temperature, speed, time, data, pressure, energy, power, angle, frequency, fuel economy and approximate cooking measures (cups, tablespoons and teaspoons to grams for common ingredients) — across metric, US and UK systems.`,
  how: `Choose the category of unit you're converting (length, weight, and so on), enter your value, and select the units to convert from and to. The result updates instantly.`,
  tip: `Double-check which specific unit variant you need for things like fluid ounces or tons — US and UK definitions differ for several common units, and picking the wrong one gives a subtly wrong answer. Cooking conversions between volume and grams are approximate, because a cup of flour weighs different amounts depending on how it is scooped.`,
};

export default function UnitConverter() {
  // /tool/unit-converter/cm-to-inches opens with that pair selected. The page always opens with 1 filled in so an answer
  // is on screen before anyone types; seeing it costs nothing (only copying, printing or saving the result counts).
  const pair = pairBySlug(useParams<{ pair?: string }>().pair);
  // A shared link (?c=temperature&from=celsius&to=fahrenheit&v=-40) restores what was on screen; a pair page's own
  // pair wins for the units, and anything in the link that does not exist is ignored.
  const [linked] = useState(() => (typeof window === "undefined" ? {} : parseUnitSearch(window.location.search)));
  const linkOk = !pair || linked.category === pair.category;
  const [category, setCategory] = useState(pair?.category ?? linked.category ?? "length");
  const [from, setFrom] = useState(pair?.from ?? linked.from ?? "");
  const [to, setTo] = useState(pair?.to ?? linked.to ?? "");
  const [value, setValue] = useState((linkOk ? linked.value : undefined) ?? "1");
  const [ingredient, setIngredient] = useState(linked.ingredient ?? COOKING_INGREDIENTS[0].id);

  const cat = category === "cooking" ? cookingCategory(ingredient) : CATEGORIES.find(c => c.id === category) ?? CATEGORIES[0];
  const names = cat.units.map(u => u.name);
  const fromUnit = names.includes(from) ? from : names[0];
  const toUnit = names.includes(to) ? to : names[1] ?? names[0];

  const v = Number(value);
  const hasValue = value.trim() !== "" && Number.isFinite(v);
  // A temperature below absolute zero (0 K) cannot exist, so it is refused instead of converted.
  const belowAbsoluteZero = hasValue && category === "temperature" && names.includes("kelvin") && convertValue(cat, fromUnit, "kelvin", v) < -1e-9;
  const out = hasValue && !belowAbsoluteZero ? convertValue(cat, fromUnit, toUnit, v) : NaN;
  const result = Number.isFinite(out) ? formatNumber(out) : "";

  useLogToolUsage(result && !belowAbsoluteZero ? result : null);

  // Keep the address in step with the page so a copied link (or a reload) comes back to the same conversion.
  // replaceState: typing must not fill the Back button with one entry per keystroke.
  const opened = useRef(false);
  useEffect(() => {
    if (!opened.current) { opened.current = true; return; } // opening the page leaves its address alone
    try {
      const search = buildUnitSearch({ category, from, to, value, ingredient, fromUnit, toUnit });
      if (window.location.search !== search) window.history.replaceState(window.history.state, "", `${window.location.pathname}${search}${window.location.hash}`);
    } catch { /* an address that cannot be updated never blocks the converter */ }
  }, [category, from, to, value, ingredient, fromUnit, toUnit]);
  const swap = () => { setFrom(toUnit); setTo(fromUnit); };

  return (
    <ToolContainer>
      <Tabs value={category} onValueChange={(c) => { setCategory(c); setFrom(""); setTo(""); }}>
        <TabsList className="mb-4 flex-wrap h-auto justify-start">
          {CATEGORIES.map(c => <TabsTrigger key={c.id} value={c.id}>{c.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {category === "cooking" && (
        <div className="mb-4 max-w-sm">
          <Label>Ingredient</Label>
          <Select value={ingredient} onValueChange={setIngredient}>
            <SelectTrigger aria-label="Ingredient"><SelectValue /></SelectTrigger>
            <SelectContent>{COOKING_INGREDIENTS.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="cooking-note">Approximate. Grams per cup change with the brand and with how the cup is filled (spooned, scooped, sifted or packed).</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
        <div>
          <Label>From</Label>
          <Select value={fromUnit} onValueChange={setFrom}>
            <SelectTrigger aria-label="From unit"><SelectValue /></SelectTrigger>
            <SelectContent>{names.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          {/* The decimal keypad on iOS has no minus key, so categories that take negative values (temperature, angle) keep the default keyboard. */}
          <Input type="number" inputMode={cat.negative ? undefined : "decimal"} value={value} onChange={e => setValue(e.target.value)} placeholder="Enter value" className="mt-2" aria-label="Value" />
        </div>
        <Button variant="ghost" size="icon" onClick={swap} aria-label="Swap units" className="mb-1"><ArrowLeftRight className="h-4 w-4" /></Button>
        <div>
          <Label>To</Label>
          <Select value={toUnit} onValueChange={setTo}>
            <SelectTrigger aria-label="To unit"><SelectValue /></SelectTrigger>
            <SelectContent>{names.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <div className="mt-2 p-2.5 rounded-md border bg-muted/50 text-lg font-medium min-h-[42px]" data-testid="unit-result">{result || "—"}</div>
          {result && !belowAbsoluteZero && <p data-result className="sr-only">{formatNumber(v)} {fromUnit} = {result} {toUnit}</p>}
        </div>
      </div>
      {belowAbsoluteZero && <p role="alert" className="mt-3 text-sm text-destructive">That is below absolute zero (0 K, −273.15 °C, −459.67 °F), the lowest temperature possible.</p>}
      {hasValue && !belowAbsoluteZero && (
        <div className="mt-6" data-testid="unit-all" data-result>
          <p className="text-sm font-medium mb-2">{formatNumber(v)} {fromUnit} equals</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {cat.units.filter(u => u.name !== fromUnit).map(u => (
              <li key={u.name} className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">{u.name}</span><span className="font-mono">{formatNumber(convertValue(cat, fromUnit, u.name, v))}</span></li>
            ))}
          </ul>
        </div>
      )}
      {pair && (
        <section className="mt-8" data-testid="unit-pair-info">
          <h2 className="text-lg font-semibold mb-2">How to convert {pair.label.toLowerCase()}</h2>
          <p className="text-sm text-muted-foreground mb-4" data-testid="unit-pair-formula">{pairFormula(pair)}</p>
          <table className="text-sm border-collapse w-full max-w-md" data-testid="unit-pair-table">
            <caption className="text-left text-sm font-medium mb-1">{pairH1(pair).replace(" Converter", "")}: common values</caption>
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 font-medium">{pair.from}</th><th className="font-medium">{pair.to}</th></tr></thead>
            <tbody>{pairTable(pair).map((r) => <tr key={r.from} className="border-t"><td className="py-1 tabular-nums">{r.from}</td><td className="tabular-nums">{r.to}</td></tr>)}</tbody>
          </table>
        </section>
      )}
      <nav className="mt-8" aria-label="Popular conversions" data-testid="unit-pair-links">
        <h2 className="text-lg font-semibold mb-2">Popular conversions</h2>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {UNIT_PAIRS.filter((p) => p.slug !== pair?.slug).map((p) => (
            <li key={p.slug}><Link href={`/tool/unit-converter/${p.slug}`} className="text-primary hover:underline">{p.label}</Link></li>
          ))}
        </ul>
      </nav>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
